import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  BalancesByToken,
  DropListWithIconItem,
  MarketPair,
  MarketRate,
  OrderType,
  SelectedAccount,
  TradeForm,
  WalletType,
  WalletTypeV1,
} from "@cede/types";
import {
  API_ENDPOINT,
  CEDE_CEXS,
  forgeTokenURL,
  formatBalance,
  Precise,
  QueryKeys,
  shouldOrderBookBeEnabled,
} from "@cede/utils";
import { useTradeTabs } from "../core/useTradeTabs";
import { useTradeForm } from "../form/useTradeForm";
import { useMarkets } from "./useMarkets";
import { useTradePath } from "./useTradePath";
import { BALANCE_DEFAULT_TTL } from "@/constants/trading";
import { useApi } from "@/hooks/core/useApi";
import { useConfig } from "@/hooks/core/useConfig";
import { useSupportedExchanges } from "@/hooks/core/useSupportedExchanges";
import { useOrderBook } from "@/hooks/orderbook/useOrderBook";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useOrderBookStore } from "@/store/orderBookStore";
import { AvailableAssets, UseTradeApiHook } from "@/types";
import { getExchangeAccountBalances } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

const EMPTY_RATE = { bid: "0", ask: "0" };

const EMPTY_ASSETS_FROM: AvailableAssets = [];

export const useTradeApi: UseTradeApiHook = (params: {
  selectedAccount?: SelectedAccount;
  dispatchForm: (action: { field: keyof TradeForm | "revertToken"; value?: TradeForm[keyof TradeForm] }) => void;
  tokenFrom: DropListWithIconItem | null;
  form: TradeForm | null;
}) => {
  const apiHelper = useApi();
  const { hubUser, auth } = useHubUser();

  const { selectedAccount, tokenFrom, form } = params;
  const { supportedExchangesById } = useSupportedExchanges();
  const orderType = useTradeTabs((state) => state.selectedTab);
  const { config } = useConfig();
  const { tradeForm } = useTradeForm();
  const widgetConfig = tradeForm.initialProps;

  // Get the current campaign from URL to detect stale store data
  const { id: currentCampaignFromUrl } = useParams();

  // Use the shared markets hook for consistent caching
  const account = hubUser?.exchangeAccounts.find((acc) => acc.id === Number(selectedAccount?.value));
  const { markets } = useMarkets(account?.exchangeId, selectedAccount?.value);

  // It's used to avoid having an empty assetsTo while the new droplistDestinationTokens are loading
  // which could cause side effects like flickering
  const prevAssetsTo = useRef<AvailableAssets>([]);

  const isTradeSquareSupported =
    selectedAccount?.cexValue &&
    supportedExchangesById[selectedAccount?.cexValue]?.supportedFeatures.includes("trade_square") &&
    orderType === OrderType.MARKET;
  // per cex limit order support
  const cexSupportsLimitOrder = widgetConfig?.limitOrderExchangeIds
    ? widgetConfig?.limitOrderExchangeIds.includes(selectedAccount?.cexValue || "")
    : true;

  // general limit order support
  const isLimitOrderSupported = config ? !!config?.limit_order_ui && cexSupportsLimitOrder : undefined;

  const shouldUseTradeSquare = Boolean(form?.tokenFrom && isTradeSquareSupported && params.form?.noCommonMarket);

  const {
    droplistDestinationTokens,
    isLoading: droplistDestinationTokensAreLoading,
    error: droplistDestinationTokensError,
    tradePath,
    tradePathBySupportedTokens,
  } = useTradePath({
    accountId: selectedAccount?.value || "",
    fromToken: tokenFrom?.value || "",
    toToken: form?.tokenTo?.value,
    enabled: !!isTradeSquareSupported,
  });

  // Validate that the selected exchange belongs to the current campaign
  // This prevents stale orderbook requests when navigating between campaigns
  const isExchangeValidForCampaign =
    !widgetConfig?.exchangeIds ||
    widgetConfig.exchangeIds.length === 0 ||
    Boolean(selectedAccount?.cexValue && widgetConfig.exchangeIds.includes(selectedAccount.cexValue));

  // Check if the store's campaign matches the current URL's campaign
  // This is the most reliable way to detect stale data when navigating between campaigns
  const isStoreMatchingCurrentCampaign = useMemo(() => {
    // If we're not on a campaign page (no id param), allow all requests
    if (!currentCampaignFromUrl) return true;
    // If widgetConfig doesn't have campaign metadata, it's likely not initialized yet
    if (!widgetConfig?.campaignMetadata?.name) return false;
    // Check if the store's campaign matches the URL
    return widgetConfig.campaignMetadata.name === currentCampaignFromUrl;
  }, [currentCampaignFromUrl, widgetConfig?.campaignMetadata?.name]);

  // Use the centralized useOrderBook hook
  const { orderBookMetrics } = useOrderBook({
    exchangeId: selectedAccount?.cexValue || "",
    pairSymbol: form?.market?.pairSymbol || "",
    enabled:
      !!selectedAccount?.cexValue &&
      !!form?.market?.pairSymbol &&
      !shouldUseTradeSquare &&
      isExchangeValidForCampaign &&
      isStoreMatchingCurrentCampaign &&
      shouldOrderBookBeEnabled(form?.market?.pairSymbol || "", widgetConfig?.recommendedToken || ""),
  });

  /**
   * Fetches FROM tokens - ALL tradeable tokens from markets, enriched with user balances
   * Shows ALL tokens (not just ones user owns), with tokens that have balance sorted on top
   */
  const _fetchPortfolioAssetOfSelectedCex = async () => {
    if (selectedAccount && auth.identityToken && account && markets.length > 0) {
      // Fetch balances directly from API
      const walletTypes = selectedAccount.cexValue === CEDE_CEXS.BYBIT ? [WalletType.UNIFIED] : [WalletTypeV1.SPOT];
      const groupedBalances = await getExchangeAccountBalances(auth, selectedAccount.value, walletTypes, true);

      // Extract balances for the specific account
      const accountBalances: BalancesByToken = groupedBalances[selectedAccount.value] || {};

      // Use markets from the hook instead of making duplicate API call
      // Get ALL tradeable tokens from markets (both base and quote)
      const allTradeableTokens = new Set<string>();
      const activeSpotMarkets = markets.filter((market) => market.type === "spot" && market.active);

      activeSpotMarkets.forEach((market) => {
        // Add both base and quote tokens so user can sell either
        allTradeableTokens.add(market.base);
        allTradeableTokens.add(market.quote);
      });

      // Create asset list from ALL tradeable tokens, enriched with user balances
      const assetPortfolio: AvailableAssets = Array.from(allTradeableTokens)
        .map((tokenSymbol) => {
          const balance = accountBalances[tokenSymbol];
          const searchableTerms = [tokenSymbol];

          if (balance?.tokenName) {
            searchableTerms.push(balance.tokenName);
          }

          const asset: AvailableAssets[number] = {
            img: forgeTokenURL(tokenSymbol),
            label: tokenSymbol,
            value: tokenSymbol,
            balance: balance ? formatBalance(balance.freeBalance || 0) : "0",
            freeBalance: balance?.freeBalance || 0,
            amount: balance ? balance.refFreeBalance.toFixed(2) : "0",
            ...(searchableTerms && {
              searchableTerms,
            }),
          };
          return asset;
        })
        // Sort by balance (highest first), then alphabetically
        .sort((a, b) => {
          const amountA = Number(a.amount);
          const amountB = Number(b.amount);

          // Both have balance or both have no balance - sort by amount/alphabetically
          if ((amountA > 0 && amountB > 0) || (amountA === 0 && amountB === 0)) {
            if (amountB !== amountA) {
              return amountB - amountA;
            }
            // If amounts are equal, sort alphabetically
            return a.label.localeCompare(b.label);
          }

          // Tokens with balance come before tokens without balance
          return amountB > 0 ? 1 : -1;
        });
      return assetPortfolio;
    }

    return [];
  };

  const { data: assetsFrom, isLoading: isLoadingBalances } = useQuery({
    queryKey: [QueryKeys.ACCOUNT_BALANCES, selectedAccount?.value, auth.identityToken, markets.length],
    queryFn: _fetchPortfolioAssetOfSelectedCex,
    staleTime: BALANCE_DEFAULT_TTL,
    enabled:
      Object.keys(supportedExchangesById).length > 0 &&
      !!selectedAccount?.value &&
      !!auth.identityToken &&
      !!account &&
      markets.length > 0,
    refetchOnWindowFocus: true,
  });

  /**
   * Helper to get account
   */
  const getAccount = useCallback(
    (accountId: string) => {
      return hubUser?.exchangeAccounts.find((acc) => acc.id === Number(accountId));
    },
    [hubUser],
  );

  /**
   * Computes the price for the selected trade path. The amount is in the destination token.
   */
  const computePriceForTradePath = async () => {
    let price = "0";
    if (!tradePath || !selectedAccount || !hubUser || !auth.identityToken) return price;

    const account = getAccount(selectedAccount.value);
    if (!account) return price;

    for (const path of tradePath) {
      const marketRate = (await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
        queryParams: { pairSymbol: path.pairSymbol, exchangeId: account.exchangeId },
      })) as MarketRate;
      const sellPriceInDestinationToken =
        path.orderSide === "sell" ? marketRate.bid : Precise.stringDiv("1", marketRate.ask);
      price =
        price === "0"
          ? sellPriceInDestinationToken || "0"
          : Precise.stringMul(price, sellPriceInDestinationToken || "0") || "0";
    }
    return price;
  };

  /**
   * Fetches the market price for the selected pair or for the trade path.
   * For buy orders, the price is the ask price.
   * For sell orders, the price is the bid price.
   */
  const { data: marketRate } = useQuery({
    queryKey: ["updateMarketRate", tradePath, form?.market?.pairSymbol, selectedAccount?.value, auth.identityToken],
    queryFn: async () => {
      if (!selectedAccount || !hubUser || !auth.identityToken) return EMPTY_RATE;

      if (!form?.market && tradePath) {
        const tradePathPrice = await computePriceForTradePath();
        return {
          bid: tradePathPrice.toString(),
          ask: tradePathPrice.toString(),
        };
      }

      const account = getAccount(selectedAccount.value);
      if (!account) return EMPTY_RATE;

      // Check if we have the market rate in the order book store (cache)
      const orderBook = useOrderBookStore.getState().orderBook;
      if (
        orderBook &&
        orderBook.pairSymbol === form?.market?.pairSymbol &&
        orderBook.exchangeId === account.exchangeId
      ) {
        const marketRate: MarketRate = {
          bid: String(orderBook.bids[0]?.[0] || 0),
          ask: String(orderBook.asks[0]?.[0] || 0),
        };
        return marketRate;
      }

      // Fallback to API
      if (!form?.market?.pairSymbol) return { bid: "0", ask: "0" };
      return await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE, {
        queryParams: {
          pairSymbol: form.market.pairSymbol,
          exchangeId: account.exchangeId,
        },
      });
    },
    staleTime: BALANCE_DEFAULT_TTL / 2,
    refetchInterval: BALANCE_DEFAULT_TTL / 2,
    enabled:
      !!hubUser &&
      !!auth.identityToken &&
      selectedAccount !== null &&
      (form?.market?.pairSymbol !== undefined || (tradePath?.length || 0) > 0),
  });

  /**
   * Derives TO tokens - all tradeable tokens based on selected FROM token
   * These are ALL tokens the user CAN BUY/TRADE TO (not limited to what they own)
   *
   * Logic:
   * - If TradeSquare supported: All tokens reachable via trade paths
   * - Otherwise: Only tokens with direct market pairs to FROM token
   */
  const { assetsTo, underlyingMarkets, assetsToAreLoading, isLoadingUnderlyingMarkets } = useMemo<{
    underlyingMarkets: MarketPair[];
    assetsTo: AvailableAssets;
    assetsToAreLoading: boolean;
    isLoadingUnderlyingMarkets: boolean;
  }>(() => {
    if (!tokenFrom) {
      return {
        assetsTo: [],
        underlyingMarkets: [],
        assetsToAreLoading: false,
        isLoadingUnderlyingMarkets: false,
      };
    }

    if (!markets.length) {
      return {
        assetsTo: [],
        underlyingMarkets: [],
        assetsToAreLoading: true,
        isLoadingUnderlyingMarkets: true,
      };
    }
    let tokensAvailable: AvailableAssets = [];
    const underlyingMarkets: MarketPair[] = markets.filter(
      (market) => market.base === tokenFrom.label || market.quote === tokenFrom.label,
    );

    // If trade square feature supported, we allow the user to select all the available destination tokens
    if (isTradeSquareSupported) {
      // If the destination tokens are loading, we return the previous assetsTo
      // to avoid the flickering of the droplist
      if (droplistDestinationTokensAreLoading && !droplistDestinationTokens?.length) {
        return {
          assetsTo: prevAssetsTo.current,
          underlyingMarkets,
          assetsToAreLoading: true,
          isLoadingUnderlyingMarkets: false,
        };
      }
      tokensAvailable = (droplistDestinationTokens || []).map((token) => {
        // We use assets owned by the user to get the balance
        const assetOwnedByUser = assetsFrom?.find((asset) => asset.value === token.value);

        const asset: AvailableAssets[number] = {
          value: token.value,
          label: token.label,
          img: forgeTokenURL(token.value),
          balance: assetOwnedByUser?.balance || "0",
          freeBalance: assetOwnedByUser?.freeBalance || 0,
          amount: assetOwnedByUser?.amount || "0",
        };
        return asset;
      });
    } else {
      // If trade square feature not supported, we allow the user to select only the destination tokens
      // which have a common market with the source token
      tokensAvailable = underlyingMarkets.map((market) => {
        const tokenSymbol = market.base === tokenFrom.label ? market.quote : market.base;

        const searchableTerms = [tokenSymbol];

        // We use assets owned by the user to get the balance
        const assetOwnedByUser = assetsFrom?.find((asset) => asset.value === tokenSymbol);

        const asset: AvailableAssets[number] = {
          value: tokenSymbol,
          label: tokenSymbol,
          img: forgeTokenURL(tokenSymbol),
          ...(searchableTerms && {
            searchableTerms,
          }),
          balance: assetOwnedByUser?.balance || "0",
          freeBalance: assetOwnedByUser?.freeBalance || 0,
          amount: assetOwnedByUser?.amount || "0",
        };
        return asset;
      });
    }

    const newAssetsTo = tokensAvailable.filter(
      (obj, index, self) => index === self.findIndex((t) => t["value"] === obj["value"]),
    );

    // keep only the assets that are SUI <-> USDT pairs on Crypto.com
    const filteredAssetsTo = newAssetsTo.filter((asset) => {
      const isCryptoCom = selectedAccount?.cexValue === CEDE_CEXS.CRYPTOCOM;
      const isSuiTokenFrom = tokenFrom?.value === "SUI";
      return isCryptoCom && isSuiTokenFrom ? asset.value === "USDT" : true;
    });

    return {
      assetsTo: filteredAssetsTo.sort((a, b) => Number(b.amount) - Number(a.amount)),
      underlyingMarkets,
      assetsToAreLoading: false,
      isLoadingUnderlyingMarkets: false,
    };
  }, [markets, tokenFrom, assetsFrom, isTradeSquareSupported, droplistDestinationTokens, selectedAccount]);

  useEffect(() => {
    prevAssetsTo.current = assetsTo;
  }, [assetsTo]);

  return {
    isLoadingBalances,
    isLoadingUnderlyingMarkets,
    assetsFrom: assetsFrom ?? EMPTY_ASSETS_FROM, // FROM tokens (user's balances)
    assetsTo, // TO tokens (all tradeable tokens from markets)
    assetsToAreLoading,
    underlyingMarkets,
    orderBookMetrics,
    marketRate: marketRate ?? EMPTY_RATE,
    tradePath,
    shouldUseTradeSquare,
    droplistDestinationTokensError,
    droplistDestinationTokensAreLoading,
    markets, // All market pairs (auto-loaded via useMarkets hook)
    isLimitOrderSupported,
    tradePathBySupportedTokens,
  };
};
